import { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Image, Menu, ScrollArea } from '@mantine/core';
import { TFunction } from 'i18next';

import { Button, SectionLabel, Text } from 'components';
import { ICreditAttachment } from 'types';
import { useTranslation } from 'react-i18next';
import i18n from 'config/localization';
import { StorageUtils } from 'utils';
import { AppConstants } from '../../../constants';

const { STORAGE_USER_DOCUMENTATION_LANGUAGE, DE_FLAG, EN_FLAG, FR_FLAG, IT_FLAG, SR_FLAG } = AppConstants;

const languages = [
  { key: 'de', label: 'german', flag: DE_FLAG },
  // { key: 'it', label: 'italian', flag: IT_FLAG },
  // { key: 'fr', label: 'french', flag: FR_FLAG },
  { key: 'sr', label: 'serbian', flag: SR_FLAG },
  { key: 'en', label: 'english', flag: EN_FLAG },
];

interface IRequiredDocumentation {
  requiredAttachments: ICreditAttachment[] | undefined;
  t: TFunction;
}

interface ICreditRequiredDocumentationItemProps {
  item: ICreditAttachment;
  language: string;
}

const RequiredDocumentation = ({
  requiredAttachments,
  t,
}: IRequiredDocumentation) => {
  const initialLang = i18n.language;
  const [language, setLanguage] = useState<string>(initialLang);

  useEffect(() => {
    const storageLanguage = StorageUtils.getString(STORAGE_USER_DOCUMENTATION_LANGUAGE);
    if (storageLanguage) {
      setLanguage(storageLanguage);
    }
  }, []);

  const changeLanguage = useCallback((lang: string) => {
    StorageUtils.setString(STORAGE_USER_DOCUMENTATION_LANGUAGE, lang);
    setLanguage(lang);
  }, []);

  return (
    <Box h={300} p={'md'} bg={'#F1F2F3'}>
      <Flex align={'center'} mr={'md'}>
        <SectionLabel label={t('credit.documentation.required')} variant={'green'} pb={'sm'} />
        <Menu>
          <Menu.Target>
            <Button variant={'transparent'} pb={'sm'}>
              <Image src={languages.find(el => el.key === language)?.flag} height={14} width={14} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {languages.map((el, index) => (
              <Menu.Item leftSection={<Image src={el.flag} height={14} width={14} />} key={`language-${index}`} onClick={() => changeLanguage(el.key)}>
                {t(`language.${el.label}`)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Flex>
      <ScrollArea
        h={240}
        type={'scroll'}
        overscrollBehavior={'contain'}
      >
        {requiredAttachments?.map((item) => <CreditRequiredDocumentationItem item={item} language={language} />)}
      </ScrollArea>
    </Box>
  )
}

const CreditRequiredDocumentationItem = ({
  item,
  language,
}: ICreditRequiredDocumentationItemProps) => {
  const { t } = useTranslation('translation', { lng: language.toLowerCase() });

const getName = useCallback(() => {
  let localizedMessage = item.name;
  if (item.localizationKey) {
    localizedMessage = t(`credit.documentation.type.${item.localizationKey}`);
    if (item.localizationParams) {
      let object: any = {};
      item.localizationParams.split(',').forEach((el, index) => {
        object[`date${index}`] = new Date(el);
      });
      localizedMessage = t(`credit.documentation.type.${item.localizationKey}`, object).toString();
    }

    if (item.additionalLocalizationKey) {
      let additional = '';
      item.additionalLocalizationKey.split(',').forEach(el => {
        if (additional.length > 0) {
          additional += ', ';
        }
        additional += t(`credit.documentation.type.additional.${item.type.toLowerCase()}.${el}`)
      });
      localizedMessage += ' (' + additional + ')';
    }
    return localizedMessage;
  }
  return item.name;
}, [item, language, t]);

  return (
    <Text
      key={`attachment-${item.id}`}
      c={item.delivered ? 'gray' : '#378F0E'}
      pb={'xs'}
      fw={item.delivered ? 'bold' : undefined}
    >
      {getName()}
    </Text>
  )
}

export default RequiredDocumentation;